// app/components/AdvancedChart/hooks/useBuiltInIndicator.ts
import { useEffect, useRef, useState } from 'react'
import { ISeriesApi } from 'lightweight-charts'
import { plotSelectedIndicator } from '../utils/plotSelectedIndicator'
import { Candle } from '../types'
import { IChartApi } from 'lightweight-charts'

// Interface for enhanced indicator result
interface IndicatorResult {
  mainSeries: ISeriesApi<'Line'> | null
  additionalSeries: ISeriesApi<'Line'>[]
  alertInfo?: {
    currentValue: number
    alertText: string
    alertColor: string
  }
  alertElement?: HTMLElement
}

/**
 * This hook watches for changes in the selected indicator and chart data,
 * and calls plotSelectedIndicator to plot the indicator on the chart.
 * Returns the indicator result and alert information.
 */
export function useBuiltInIndicator(
  chart: IChartApi | null,
  candleData: Candle[],
  selectedIndicator: string | null
) {
  const indicatorResultRef = useRef<IndicatorResult | null>(null)
  const [alertInfo, setAlertInfo] = useState<IndicatorResult['alertInfo'] | undefined>()

  useEffect(() => {
    if (!chart || candleData.length === 0) {
      return
    }

    if (!selectedIndicator) {
      // Clean up existing indicator when selectedIndicator is null
      if (indicatorResultRef.current) {
        try {
          if (indicatorResultRef.current.mainSeries) {
            chart.removeSeries(indicatorResultRef.current.mainSeries)
          }
          indicatorResultRef.current.additionalSeries.forEach(series => {
            chart.removeSeries(series)
          })
          // Remove alert element
          if (indicatorResultRef.current.alertElement && indicatorResultRef.current.alertElement.parentNode) {
            indicatorResultRef.current.alertElement.parentNode.removeChild(indicatorResultRef.current.alertElement)
          }
        } catch (error) {
          console.warn('Failed to remove indicator series:', error)
        }
        indicatorResultRef.current = null
      }
      setAlertInfo(undefined)
      return
    }

    // Plot the selected indicator
    const newResult = plotSelectedIndicator(
      chart,
      candleData,
      selectedIndicator,
      indicatorResultRef.current
    )

    indicatorResultRef.current = newResult
    setAlertInfo(newResult?.alertInfo)
  }, [chart, candleData, selectedIndicator])

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      if (indicatorResultRef.current && chart) {
        try {
          if (indicatorResultRef.current.mainSeries) {
            chart.removeSeries(indicatorResultRef.current.mainSeries)
          }
          indicatorResultRef.current.additionalSeries.forEach(series => {
            chart.removeSeries(series)
          })
          // Remove alert element
          if (indicatorResultRef.current.alertElement && indicatorResultRef.current.alertElement.parentNode) {
            indicatorResultRef.current.alertElement.parentNode.removeChild(indicatorResultRef.current.alertElement)
          }
        } catch (error) {
          console.warn('Failed to remove indicator series on cleanup:', error)
        }
      }
    }
  }, [chart])

  return {
    indicatorSeries: indicatorResultRef.current?.mainSeries || null,
    alertInfo
  }
}
